import { Component, ElementRef, HostListener, signal, ViewChild, WritableSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from '@app/auth.service';
import { ILoginData } from '@app/lib/models/login.model';
import { AssignedTasksComponent } from './assigned-tasks/assigned-tasks.component';
import { HeaderComponent } from "./header/header.component";
import { IMenuItem, MAIN_MENUS } from './models/main-menu.model';
import { ThemeService } from './services/theme.service';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { environment } from '@env/environment';
import { DialogService } from '@app/lib/services/dialog.service';
import { ECourtId } from '@app/dashboard/models/constant';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    RouterOutlet,
    AssignedTasksComponent,
    HeaderComponent,
    RouterLink,
    CommonModule,
    MatIconModule,
    FormsModule,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit {

  @ViewChild(AssignedTasksComponent) assignedTasks!: AssignedTasksComponent;

  // Layout
  sidebarWidth = 325;
  resizing = false;
  minWidth = 300;
  maxWidth = 700;

  // Mobile State
  isMobile = false;
  isMobileMenuOpen = false;
  isSidebarOpen = true;
  oldVersionUrl = environment.oldVersionUrl;

  isSupremeCourtFlg: boolean = false;
  isCOAFlg: boolean = false
  // ✅ FIX: Changed type to 'any' to avoid string vs number issues in API data
  mobileSubMenuId: any = null;

  // Menu
  menuItems: WritableSignal<IMenuItem[]> = signal([]);
  expandedMenu: any = null;
  hideTimeout: any;
  REPORTS_MENU_ID = 6;

  // Search
  globalSearch: string = "";
  searchResults: any[] = [];
  MIN_SEARCH_LENGTH = 3;
  @ViewChild('searchArea') searchArea!: ElementRef;

  // Route animation reference and state
  @ViewChild('routeWrapper') routeWrapper!: ElementRef;
  isRouteAnimating = false;
  clickX = 0;
  clickY = 0;

  // Click Ripple State
  showRipple = false;
  rippleX = 0;
  rippleY = 0;

  // Layout calculations
  arrowPosition: number = 0;

  constructor(
    private themeService: ThemeService,
    private authService: AuthService,
    private router: Router,
    private dialogService: DialogService
  ) {
    this.authService.user
      .pipe(takeUntilDestroyed())
      .subscribe((userData: ILoginData | null) => {
        if (userData) {
          this.updateTheme(userData.courtId);
          this.updateAuthorizedMenuList(userData);
          this.authService.startTessionTimer();
        }
      });

    // Listen to router navigation events to trigger reveal animation for all route changes (task & menu)
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.triggerRouteAnimation();
      });
  }

  ngOnInit() {
    this.checkMobileView();
    //console.log(this.oldVersionUrl)
    this.getCourtNameData()
  }

  // ==========================================
  // 🟢 EXISTING MENU FILTER LOGIC
  // ==========================================
  private updateAuthorizedMenuList(userData: ILoginData) {
    const allowedMenuNames: Record<number, string> = {};
    const allowedIds = new Set<number>([0]);

    const collectAllowed = (dtos: any[] = []) => {
      for (const dto of dtos) {
        allowedIds.add(dto.menuId);
        if (dto.menuName != null) allowedMenuNames[dto.menuId] = dto.menuName;
        if (Array.isArray(dto.subMenus) && dto.subMenus.length) {
          collectAllowed(dto.subMenus);
        }
      }
    };

    collectAllowed(userData.menuDTOList ?? []);

    const hasHandler = (h: unknown): boolean => {
      if (h === true) return true;
      if (h === false || h == null) return false;
      if (typeof h === 'string') return h.trim().length > 0;
      if (typeof h === 'number') return h === 1;
      return !!h;
    };

    const convertAndPrune = (menuDTOList: any[] = [], rootParentSlug: string = ''): IMenuItem[] => {
      const out: IMenuItem[] = [];

      for (const dto of menuDTOList) {
        if (!allowedIds.has(dto.menuId)) continue;

        let currentSlug = dto.menuName
          ? dto.menuName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '')
          : '';

        // If no rootParentSlug was passed down, this node is effectively the top-level root for its branch
        const isRootNode = !rootParentSlug;
        const passedDownSlug = isRootNode ? currentSlug : rootParentSlug;

        const children = convertAndPrune(dto.subMenus ?? [], passedDownSlug);

        const keep = hasHandler(dto?.handler) || children.length > 0 || dto.menuName === 'Monitoring';

        if (!keep) continue;

        const mainMenu = MAIN_MENUS.find(m => m.id === dto.menuDescription);

        let routerLink = mainMenu?.routerLink ?? '';

        // Universal Fallback: If it's a leaf node but has no route link, generate one automatically.
        if (!routerLink && !(children.length > 0)) {
          // If we have a root parent slug (meaning we are nested), prefix it
          if (passedDownSlug && !isRootNode) {
            routerLink = `${passedDownSlug}/${currentSlug}`;
          } else {
            // Otherwise just use the current slug
            routerLink = `${currentSlug}`;
          }
        }

        out.push({
          id: dto.menuId,
          label: allowedMenuNames[dto.menuId] ?? dto.menuName ?? mainMenu?.label ?? '',
          routerLink,
          subMenu: children.length ? children : undefined,
        });
      }
      return out;
    };

    const authorizedMenus: IMenuItem[] = convertAndPrune(userData.menuDTOList ?? []);


    this.menuItems.set(this.sortSubMenusOnly(authorizedMenus));
  }

  getCourtNameData(): ILoginData | null {
    try {
      const userData = localStorage.getItem('userData') as string;
      const userData2 = userData ? JSON.parse(userData) : null;
      const courtIdData = userData2.courtId;
      if (courtIdData && courtIdData === ECourtId.SC_ID) {
        this.isSupremeCourtFlg = true;
      } else if (courtIdData && courtIdData === ECourtId.COA_ID) {
        this.isCOAFlg = true;
      }
      return userData ? JSON.parse(userData) : null;
    } catch (err) {
      return null
    }

  }
  refreshSidebarTasks() {
    if (this.assignedTasks) {
      // Call whatever your fetch/refresh function is named inside AssignedTasksComponent
      this.assignedTasks.getTasks();
    }
  }
  private sortSubMenusOnly(list: IMenuItem[]): IMenuItem[] {
    return list.map(item => ({
      ...item,
      subMenu: item.subMenu
        ? item.subMenu
          .map(sub => this.sortSubMenusOnly([sub])[0])
          .sort((a, b) => a.label.localeCompare(b.label))
        : undefined
    }));
  }

  // ==========================================
  // 📱 Responsive Logic
  // ==========================================
  @HostListener('window:resize')
  onResize() {
    this.checkMobileView();
  }

  checkMobileView() {
    this.isMobile = window.innerWidth <= 1024;
    if (this.isMobile) {
      this.isSidebarOpen = false;
      this.isMobileMenuOpen = false;
    } else {
      this.isSidebarOpen = true;
    }
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  toggleTaskSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  // ✅ FIX: Using loose checking logic (implicitly via type 'any') ensures string/number IDs work
  toggleMobileSubMenu(menuId: any) {
    if (this.mobileSubMenuId === menuId) {
      this.mobileSubMenuId = null;
    } else {
      this.mobileSubMenuId = menuId;
    }
  }

  onMobileNavigate() {
    this.isMobileMenuOpen = false;
    this.globalSearch = '';
  }

  // ==========================================
  // 🖥️ Desktop Mega Menu Logic
  // ==========================================
  keepMenuOpen() {
    if (this.isMobile) return;
    clearTimeout(this.hideTimeout);
  }

  toggleSubMenu(menuItem: any, event: MouseEvent) {
    if (this.isMobile) return;
    this.expandedMenu = menuItem;

    setTimeout(() => {
      const target = (event.currentTarget as HTMLElement) || (event.target as HTMLElement);
      if (target) {
        const rect = target.getBoundingClientRect();
        this.arrowPosition = rect.left + (rect.width / 2);
      }
    });

    document.body.classList.add('showing-submenu');
  }

  hideSubMenu() {
    if (this.isMobile) return;
    this.hideTimeout = setTimeout(() => {
      this.expandedMenu = null;
      document.body.classList.remove('showing-submenu');
    }, 200);
  }

  getChunks(array: any[] | undefined, size: number): any[][] {
    if (!array || !array.length) return [];
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  // ==========================================
  // 🔍 Search & Click Capture Logic
  // ==========================================
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.searchArea && !this.searchArea.nativeElement.contains(event.target as Node)) {
      this.closeSearch();
    }

    const target = event.target as HTMLElement;
    if (!target) return;

    // Detect clicks on interactive items (menu items, task cards, links, buttons)
    const isClickable = target.closest('a, button, [routerLink], .cursor-pointer, app-assigned-tasks');
    if (isClickable) {
      this.captureClickCoordinates(event);
    }
  }

  filterGlobalMenu() {
    const term = this.globalSearch.trim().toLowerCase();
    if (term.length < this.MIN_SEARCH_LENGTH) {
      this.searchResults = [];
      return;
    }

    const results: any[] = [];
    const scanMenu = (menu: any, parentLabel?: string) => {
      const labelText = menu.label?.toLowerCase() || "";
      const fullLabel = parentLabel ? `${parentLabel} → ${menu.label}` : menu.label;

      if (labelText.includes(term)) {
        results.push({
          id: menu.id,
          routerLink: menu.routerLink,
          subMenu: menu.subMenu,
          fullLabel
        });
      }
      if (menu.subMenu) {
        for (const sub of menu.subMenu) scanMenu(sub, fullLabel);
      }
    };

    for (const m of this.menuItems()) scanMenu(m);
    this.searchResults = results.sort((a, b) => a.fullLabel.localeCompare(b.fullLabel));
  }


  triggerRipple(event?: MouseEvent) {
    if (!event) return;
    this.rippleX = event.clientX;
    this.rippleY = event.clientY;
    this.showRipple = false;
    setTimeout(() => {
      this.showRipple = true;
    }, 10);
    setTimeout(() => {
      this.showRipple = false;
    }, 600);
  }

  captureClickCoordinates(event: MouseEvent) {
    this.triggerRipple(event);

    if (this.routeWrapper) {
      const rect = this.routeWrapper.nativeElement.getBoundingClientRect();
      this.clickX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
      this.clickY = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
    } else {
      this.clickX = event.clientX;
      this.clickY = Math.max(0, event.clientY);
    }
  }

  triggerRouteAnimation() {
    this.isRouteAnimating = false;
    setTimeout(() => {
      this.isRouteAnimating = true;
    }, 20);

    setTimeout(() => {
      this.isRouteAnimating = false;
    }, 700);
  }

  navigateFromSearch(item: any, event?: MouseEvent) {
    if (!item) return;

    if (event) {
      this.captureClickCoordinates(event);
    }

    let route = item.routerLink;

    // If routerLink not available
    if (!route) {
      //console.log('router link:', route)
      this.dialogService.openDialog1({
        data: {
          title: "Navigation Error",
          message: "Route is not configured for this menu.",
          icon: "error",
        },
        disableClose: true,
        hasBackdrop: true,
        backdropClass: "dialog-backdrop",
      });
      this.router.navigateByUrl('/dashboard');
      return;
    }

    if (!route.startsWith('/dashboard')) {
      route = `/dashboard/${route}`;
    }

    this.router.navigateByUrl(route)
      .then(() => {
        this.closeSearch();
        if (this.isMobile) this.isMobileMenuOpen = false;
      })
      .catch(() => {
        //console.log('router link1', route)
        this.dialogService.openDialog1({
          data: {
            title: "Navigation Error",
            message: "This page is not available or route is not configured.",
            icon: "error",
          },
          disableClose: true,
          hasBackdrop: true,
          backdropClass: "dialog-backdrop",
        });
        this.router.navigateByUrl('/dashboard');
      });

  }

  closeSearch() {
    this.globalSearch = '';
    this.searchResults = [];
  }

  // ==========================================
  // 🖱️ Sidebar Resizing
  // ==========================================
  startResizing(event: MouseEvent) {
    if (this.isMobile) return;
    this.resizing = true;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.resizing) return;
    const newWidth = event.clientX;
    if (newWidth > this.minWidth && newWidth < this.maxWidth) {
      this.sidebarWidth = newWidth;
    }
  }

  @HostListener('document:mouseup')
  stopResizing() {
    this.resizing = false;
  }

  // ==========================================
  // ⚙️ Theme
  // ==========================================
  private updateTheme(courtId?: number) {
    let themeName = 'theme-light';
    switch (courtId) {
      case 1: themeName = 'theme-light'; break;
      case 2: themeName = 'theme-light'; break
      default: break;
    }
    this.themeService.switchTheme(themeName);

  }
}